<template>
  <div class="ba-ranking-card" :class="$i18n.locale">
    <template v-if="!noData">
      <div v-if="panelData.session && !isCustomNode" class="panels-wrap session">
        <h3 class="card-title"
            v-html="$t('ranking.sessionSummary.title')">
        </h3>
        <div class="panels">
          <div class="card-panel page-per-session">
            <div class="contents-wrap">
              <div class="title" :class="{'no-benchmark': stateDefault}">
                <span v-html="$t('ranking.sessionSummary.pages')"></span>
              </div>
              <div class="contents">
                <i class="icon icon--session-route"></i>
                <div class="benchmark-contents">
                  <template v-if="stateDefault">
                    <div class="contents-text" v-html="$t('benchmark.session.pvPerSession.defaultDesc')"></div>
                  </template>
                  <template v-else>
                    <div class="contents-text">
                      <span v-html="$t(`benchmark.session.pvPerSession.${compareData('session', 'pvPerSession').state}`, { data: (compareData('session', 'pvPerSession').value).toFixed(1) })"></span>
                    </div>
                  </template>
                </div>
                <div class="value-wrap single">
                  <span class="value" :class="{ long: panelData.session.pvPerSession >= 100 }">
                    {{ panelData.session.pvPerSession | fixed(1) }}
                  </span>
                  <span class="unit" v-html="$t('common.unit.pages')"></span>
                </div>
              </div>
            </div>
            <div class="benchmark-info" v-if="!stateDefault">
              <div class="category-icon">
                <i :class="`icon ${DOMAIN_CATEGORY_ICON[domainCategory.key]} size-14`"></i>
              </div>
              <div class="category-name">{{ domainCategory.subcategories[domainSubCategory].title[$i18n.locale] }} <span>{{ $t('common.avg') }}</span></div>
              <div class="benchmark-value" :data-unit="$t('common.unit.pages')">{{ benchmark.pvPerSession | fixed(1) }}</div>
            </div>
          </div>
          <div class="card-panel duration-per-session">
            <div class="contents-wrap">
              <div class="title" :class="{'no-benchmark': stateDefault}">
                <span v-html="$t('ranking.sessionSummary.avgDuration')"></span>
              </div>
              <div class="contents">
                <div class="benchmark-contents">
                  <template v-if="stateDefault">
                    <div class="contents-text" v-html="$t('benchmark.session.avgInterval.defaultDesc')"></div>
                  </template>
                  <template v-else>
                    <div class="contents-text">
                      <span v-html="$t(`benchmark.session.avgInterval.${compareData('session', 'intvlPerSession').state}`, { data1: getTimeString(compareData('session', 'intvlPerSession').value) })"></span>
                    </div>
                  </template>
                </div>
                <div class="value-wrap double">
                  <div class="value-item first">
                    <span class="unit">{{ $t('common.unit.perSession') }}</span>
                    <span class="value" :class="{ hour: panelData.session.intvlPerSession >= 3600000 }">
                      {{ getTimeString(panelData.session.intvlPerSession) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div class="benchmark-info" v-if="!stateDefault">
              <div class="category-icon">
                <i :class="`icon ${DOMAIN_CATEGORY_ICON[domainCategory.key]} size-14`"></i>
              </div>
              <div class="category-name">{{ domainCategory.subcategories[domainSubCategory].title[$i18n.locale] }} <span>{{ $t('common.avg') }}</span></div>
              <div class="benchmark-value first" >{{ getTimeString(benchmark.intvlPerSession) }}</div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="panelData.pv" class="panels-wrap pv" :class="{ full: !panelData.session }">
        <h3 class="card-title" v-if="stateDefault" v-html="$t('ranking.pvSummary.titleTop20')"></h3>
        <h3 class="card-title" v-else v-html="$t('ranking.pvSummary.title', {data: Number(getPercentage(panelData.pv.topTotalPv/infoData.pv, 1).toFixed(1)) + '%' })"></h3>

        <div class="panels">
          <div class="guide-link">
            <a :href="categoryLegendLink" target="_blank">
              {{ $t('benchmark.pv.guideText') }}
              <i class="icon icon--ba-link blue size-16"></i>
            </a>

          </div>
          <div class="card-panel pv-charts" @mouseenter="pauseInterval" @mouseleave="autoChangChart">
            <div class="chart-first" :class="{ moving: !!chartMoving, 'moving-start': chartMovingStart }">
              <div class="chart-pane" :class="currentType">
                <h5 v-html="$t(`ranking.pvSummary.${currentType}.title`)"></h5>
                <div class="benchmarked-description">
                  <div class="benchmark-text" v-if="stateDefault" v-html="$t(`benchmark.pv.description.${currentType}.default`)">
                  </div>
                  <div class="benchmark-text" v-else-if="currentType === 'interval'" v-html="$t(`benchmark.pv.description.${currentType}.${compareData('pv', currentType).state}`, {data: getTimeString(compareData('pv', 'interval').value)})">
                  </div>
                  <div class="benchmark-text" v-else v-html="$t(`benchmark.pv.description.${currentType}.${compareData('pv', currentType).state}`, {data: compareData('pv', currentType).value + '%'})">
                  </div>
                </div>
                <!-- <div class="chart-description" :class="{ 'with-warning': warning[currentType] }">
                  <i class="icon icon--warning" v-if="warning[currentType]"></i>
                  <p class="warning"
                    v-if="warning[currentType]"
                    v-html="warning[currentType]"></p>
                  <p v-html="description[currentType]"></p>
                </div> -->
                <div class="chart-value">
                  <span>{{ $t('common.avg') }}</span>
                  <em v-if="currentType === 'interval'">
                    {{ getTimeString(panelData.pv.data[currentType].avgTop20) }}
                  </em>
                  <em v-else>
                    {{ panelData.pv.data[currentType].avgTop20 | fixedInt(currentType === 'dropOff' || currentType === 'stayInCount' ? 2 : 1, 1) }}%
                  </em>
                  <i :class="`icon icon--ranking-${types[currentType]}`"></i>
                </div>
                <div class="chart" :class="{ loading: chartLoading }">
                  <div class="more">
                    <template v-if="currentType === 'interval'">
                      <div class="line benchmark" v-if="!stateDefault" :style="{ left: chartPointCalibration(panelData.pv.data[currentType].avgTop20 / 1000 / 1800 * 100) + '%' }"></div>
                      <div class="text benchmark" v-if="!stateDefault"
                            :style="{ left: chartPointCalibration(panelData.pv.data[currentType].avgTop20 / 1000 / 1800 * 100) + '%' }">
                            <i :class="`icon ${DOMAIN_CATEGORY_ICON[domainCategory.key]}-blue size-14`"></i>
                        {{ $t('common.avg') }}
                        {{ getTimeString(benchmark.top20Rate.interval) }}
                      </div>
                      <template v-if="!currentChartHasOne">
                        <div class="line min"
                             :style="{ left: currentMinMax.min.range / 1800 * 100 + '%' }"></div>
                        <div class="line avg"
                             :style="{ left: chartPointCalibration(panelData.pv.data[currentType].avgTop20 / 1000 / 1800 * 100) + '%' }"></div>
                        <div class="line max"
                             :style="{ left: currentMinMax.max.range / 1800 * 100 + '%' }"></div>
                        <div class="text min"
                             :style="{ left: currentMinMax.min.range / 1800 * 100 + '%' }">
                          {{ $t('common.min') }}
                          {{ getTimeString(currentMinMax.min.value * 1000) }}
                        </div>
                        <div class="text avg"
                             :style="{ left: chartPointCalibration(panelData.pv.data[currentType].avgTop20 / 1000 / 1800 * 100) + '%' }">
                          {{ $t('common.avg') }}
                          {{ getTimeString(panelData.pv.data[currentType].avgTop20) }}
                        </div>
                        <div class="text max"
                             :style="{ left: currentMinMax.max.range / 1800 * 100 + '%' }">
                          {{ $t('common.max') }}
                          {{ getTimeString(currentMinMax.max.value * 1000) }}
                        </div>
                      </template>
                      <template v-else>
                        <div class="line avg"
                             :style="{ left: chartPointCalibration(panelData.pv.data[currentType].avgTop20 / 1000 / 1800 * 100) + '%' }"></div>
                        <div class="text avg"
                             :style="{ left: chartPointCalibration(panelData.pv.data[currentType].avgTop20 / 1000 / 1800 * 100) + '%' }">
                          {{ getTimeString(panelData.pv.data[currentType].avgTop20) }}
                        </div>
                      </template>
                    </template>
                    <template v-else>
                      <div class="line benchmark" v-if="!stateDefault" :style="{ left: Number(getPercentage(benchmark.top20Rate[currentType], 1).toFixed(1)) + '%' }"></div>
                      <div class="text benchmark" v-if="!stateDefault"
                            :style="{ left: Number(getPercentage(benchmark.top20Rate[currentType], 1).toFixed(2)) + '%' }">
                            <i :class="`icon ${DOMAIN_CATEGORY_ICON[domainCategory.key]}-blue size-14`"></i>
                        {{ $t('common.avg') }}
                        {{ Number(getPercentage(benchmark.top20Rate[currentType], 1)) | fixedInt(currentType === 'dropOff' || currentType === 'stayInCount' ? 2 : 1, 1) }} %
                      </div>
                      <template v-if="!currentChartHasOne">
                        <div class="line min" :style="{ left: currentMinMax.min.range + '%' }"></div>
                        <div class="line avg" :style="{ left: chartPointCalibration(panelData.pv.data[currentType].avgTop20) + '%' }"></div>
                        <div class="line max" :style="{ left: currentMinMax.max.range + '%' }"></div>
                        <div class="text min" :style="{ left: currentMinMax.min.range + '%' }">
                          {{ $t('common.min') }}
                          {{ currentMinMax.min.value | fixedInt(currentType === 'dropOff' || currentType === 'stayInCount' ? 2 : 1, 1) }} %
                        </div>
                        <div class="text avg" :style="{ left: chartPointCalibration(panelData.pv.data[currentType].avgTop20) + '%' }">
                          {{ $t('common.avg') }}
                          {{ panelData.pv.data[currentType].avgTop20 | fixedInt(currentType === 'dropOff' || currentType === 'stayInCount' ? 2 : 1, 1) }} %
                        </div>
                        <div class="text max" :style="{ left: currentMinMax.max.range + '%' }">
                          {{ $t('common.max') }}
                          {{ currentMinMax.max.value | fixedInt(currentType === 'dropOff' || currentType === 'stayInCount' ? 2 : 1, 1) }} %
                        </div>

                      </template>
                      <template v-else>
                        <div class="line avg" :style="{ left: chartPointCalibration(panelData.pv.data[currentType].avgTop20) + '%' }"></div>
                        <div class="text avg" :style="{ left: chartPointCalibration(panelData.pv.data[currentType].avgTop20) + '%' }">
                          {{ panelData.pv.data[currentType].avgTop20 | fixedInt(currentType === 'dropOff' || currentType === 'stayInCount' ? 2 : 1, 1) }} %
                        </div>
                      </template>
                    </template>
                  </div>
                  <div class="chart-wrap">
                    <app-chart v-if="currentChartOption && !this.chartLoading && !this.chartMoving"
                              class="app-chart"
                              :chartOptions="currentChartOption"
                              :width="'100%'"
                              :height="'100%'"
                              :key="chartKey"
                              chartType="line" />
                  </div>
                </div>
              </div>
              <div class="chart-pane old"
                   v-if="formerData.length && currentType !== formerData[0]"
                   :class="formerData[0]">
                <h5 v-html="$t(`ranking.pvSummary.${formerData[0]}.title`)"></h5>
                <div class="benchmarked-description">
                  <div class="benchmark-text" v-if="stateDefault" v-html="$t(`benchmark.pv.description.${formerData[0]}.default`)">
                  </div>
                  <div class="benchmark-text" v-else-if="currentType === 'interval'" v-html="$t(`benchmark.pv.description.${formerData[0]}.${compareData('pv', formerData[0]).state}`, {data: getTimeString(compareData('pv', 'interval').value)})">
                  </div>
                  <div class="benchmark-text" v-else v-html="$t(`benchmark.pv.description.${formerData[0]}.${compareData('pv', formerData[0]).state}`, {data: (compareData('pv', formerData[0]).value) + '%'})">
                  </div>
                </div>
                <!-- <div class="chart-description"
                     :class="{ 'with-warning': warning[formerData[0]] }">
                  <i class="icon icon--warning" v-if="warning[formerData[0]]"></i>
                  <p class="warning"
                     v-if="warning[formerData[0]]"
                     v-html="warning[formerData[0]]"></p>
                  <p v-html="description[formerData[0]]"></p>
                </div> -->
                <div class="chart-value">
                  <span>{{ $t('common.avg') }}</span>
                  <em v-if="formerData[0] === 'interval'">
                    {{ getTimeString(panelData.pv.data[formerData[0]].avgTop20) }}
                  </em>
                  <em v-else>
                    {{ panelData.pv.data[formerData[0]].avgTop20 | fixedInt(formerData[0] === 'dropOff' || formerData[0] === 'stayInCount' ? 2 : 1, 1) }}%
                  </em>
                  <i :class="`icon icon--ranking-${types[formerData[0]]}`"></i>
                </div>
              </div>
            </div><div class="chart-others">
              <div v-for="i in [1, 2, 3, 4]"
                   :key="`chart-${i}`"
                   class="chart-second"
                   :class="[`chart-${i}`, { moving: chartMoving === i, 'moving-start': chartMovingStart }]"
                   @click="changeChart(currentData[i])">
                <div class="chart-pane"
                     :class="currentData[i]">
                  <h5>
                    {{ $t(`ranking.pvSummary.${currentData[i]}.title`) }}
                    <span class="gap--warning"
                          v-if="warning[currentData[i]]"></span>
                    <!-- <i class="icon icon--warning" v-if="warning[currentData[i]]"></i> -->
                  </h5>
                  <div class="chart-value"
                       :class="{ long: currentData[i] === 'interval' && panelData.pv.data[currentData[i]].avgTop20 > 60000 }">
                    <span>{{ $t('common.avg') }}</span>
                    <em v-if="currentData[i] === 'interval'">
                      {{ getTimeString(panelData.pv.data[currentData[i]].avgTop20) }}
                    </em>
                    <em v-else>
                      {{ panelData.pv.data[currentData[i]].avgTop20 | fixedInt(formerData[i] === 'dropOff' || formerData[i] === 'stayInCount' ? 2 : 1, 1) }}%
                    </em>
                    <i :class="`icon icon--ranking-${types[currentData[i]]}`"></i>
                  </div>
                </div>
                <div class="chart-pane old"
                     :class="formerData[i]"
                     v-if="formerData.length">
                  <h5>
                    {{ $t(`ranking.pvSummary.${formerData[i]}.title`) }}
                    <!-- <i class="icon icon--warning" v-if="warning[formerData[i]]"></i> -->
                  </h5>
                  <div class="chart-value"
                       :class="{ long: formerData[i] === 'interval' && panelData.pv.data[formerData[i]].avgTop20 > 60000 }">
                    <span>{{ $t('common.avg') }}</span>
                    <em v-if="formerData[i] === 'interval'">
                      {{ getTimeString(panelData.pv.data[formerData[i]].avgTop20) }}
                    </em>
                    <em v-else>
                      {{ panelData.pv.data[formerData[i]].avgTop20 | fixed(1) }}%
                    </em>
                    <i :class="`icon icon--ranking-${types[formerData[i]]}`"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <div v-else
         class="no-data">
      <i class="icon loading--empty"></i>
      <p class="no-data-title">
        {{ $t('common.noCollectedData') }}
      </p>
      <p class="no-data-description">
        {{ $t('ranking.changeSegment') }}
      </p>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { getPercentage, getTimeString } from '@/libs/utils'
import { DOMAIN_CATEGORY_ICON } from '@/libs/constants'

import AppChart from '@/components/chart'

export default {
  name: 'BaRankingCard',
  components: {
    AppChart
  },
  computed: {
    ...mapState([
      'journeyOptions',
      'domain'
    ]),
    isCustomNode () {
      return this.journeyOptions.isCustomNode
    },
    categoryLegendLink () {
      switch (this.$i18n.locale) {
        case 'ko':
          return 'https://forum.beusable.net/ko/post/2198 '
        case 'ja':
          return 'https://forum.beusable.net/ja/post/2259'
        case 'en':
          return 'https://forum.beusable.net/en/post/2260'
      }
      return 'https://forum.beusable.net/ko/post/2198 '
    },
    stateDefault () {
      return !this.domainCategory || !this.domainSubCategory || !this.benchmark
    },
    higherLower () {
      let result = {
        stayInCount: {},
        dropOff: {},
        rollback: {},
        reload: {},
        interval: {}
      }

      const data = this.panelData.pv.data
      for (const type of Object.keys(this.types)) {
        const avg = data[type].avgTop20
        result[type].low = 0
        result[type].high = 0
        if (data[type].top20.length > 1) {
          for (const page of data[type].top20) {
            const pageValue = this.getTop20Value(page, type)
            if (avg < pageValue) {
              result[type].high++
            } else if (avg > pageValue) {
              result[type].low++
            }
          }
        }
      }

      return result
    },
    warning () {
      let result = {
        stayInCount: null,
        dropOff: null,
        rollback: null,
        reload: null,
        interval: null
      }

      const data = this.panelData.pv.data

      if (this.higherLower['stayInCount'].low > this.higherLower['stayInCount'].high) {
        result['stayInCount'] = this.$t('ranking.pvSummary.stayInCount.warning.low')
      } else if (this.higherLower['stayInCount'].low === this.higherLower['stayInCount'].high) {
        result['stayInCount'] = this.$t('ranking.pvSummary.stayInCount.warning.same')
      }

      if (this.higherLower['dropOff'].low === this.higherLower['dropOff'].high) {
        result['dropOff'] = this.$t('ranking.pvSummary.dropOff.warning.same')
      } else if (this.higherLower['dropOff'].low < this.higherLower['dropOff'].high) {
        result['dropOff'] = this.$t('ranking.pvSummary.dropOff.warning.high')
      }

      if (this.higherLower['rollback'].low === this.higherLower['rollback'].high) {
        result['rollback'] = this.$t('ranking.pvSummary.rollback.warning.same')
      } else if (this.higherLower['rollback'].low < this.higherLower['rollback'].high) {
        result['rollback'] = this.$t('ranking.pvSummary.rollback.warning.high')
      }

      if (this.higherLower['reload'].low === this.higherLower['reload'].high) {
        result['reload'] = this.$t('ranking.pvSummary.reload.warning.same')
      } else if (this.higherLower['reload'].low < this.higherLower['reload'].high) {
        result['reload'] = this.$t('ranking.pvSummary.reload.warning.high')
      }

      if (data['interval'].top20.length > 1) {
        if (this.higherLower['interval'].low > this.higherLower['interval'].high) {
          result['interval'] = this.$t('ranking.pvSummary.interval.warning.low')
        } else if (this.higherLower['interval'].low < this.higherLower['interval'].high) {
          result['interval'] = this.$t('ranking.pvSummary.interval.warning.high')
        }
      } else {
        result['interval'] = this.$t('ranking.pvSummary.interval.warning.short')
      }

      return result
    },
    description () {
      let result = {
        stayInCount: null,
        dropOff: null,
        rollback: null,
        reload: null,
        interval: null
      }

      const data = this.panelData.pv.data
      for (const type of Object.keys(this.types)) {
        if (type === 'interval') {
          if (data[type].avgTop20 < 60000) {
            result[type] = this.$t(`ranking.pvSummary.${type}.description.short`)
          } else {
            if (data[type].top20.length > 1) {
              if (this.higherLower[type].low > this.higherLower[type].high) {
                result[type] = this.$t(`ranking.pvSummary.${type}.description.low`,
                  { count: this.higherLower[type].low })
              } else if (this.higherLower[type].low < this.higherLower[type].high) {
                result[type] = this.$t(`ranking.pvSummary.${type}.description.high`,
                  { count: this.higherLower[type].high })
              } else {
                result[type] = this.$t(`ranking.pvSummary.${type}.description.same`)
              }
            } else {
              result[type] = this.$t(`ranking.pvSummary.${type}.description.one`,
                { value: getTimeString(this.getTop20Value(data[type].top20[0], type) / 1000) })
            }
          }
        } else {
          if (data[type].top20.length > 1) {
            if (this.higherLower[type].low > this.higherLower[type].high) {
              result[type] = this.$t(`ranking.pvSummary.${type}.description.low`,
                { count: this.higherLower[type].low })
            } else if (this.higherLower[type].low < this.higherLower[type].high) {
              result[type] = this.$t(`ranking.pvSummary.${type}.description.high`,
                { count: this.higherLower[type].high })
            } else {
              result[type] = this.$t(`ranking.pvSummary.${type}.description.same`)
            }
          } else {
            result[type] = this.$t(`ranking.pvSummary.${type}.description.one`,
              { value: data[type].top20[0].percent.toFixed(1) })
          }
        }
      }

      return result
    },
    pageCount () {
      return this.panelData.pv.data.dropOff.top20.length
    },
    currentType () {
      return this.currentData[0]
    },
    currentMinMax () {
      const rawData = this.panelData.pv.data[this.currentType].top20
      let min = 100
      let max = 0

      if (this.currentType === 'interval') {
        min = 1800
        for (const raw of rawData) {
          let rawValue = this.getTop20Value(raw, this.currentType)
          if (rawValue === 1800000) {
            rawValue -= 1
          }
          min = Math.min(rawValue / 1000, min)
          max = Math.max(rawValue / 1000, max)
        }

        return {
          min: {
            range: Math.floor(min / 90) * 90 + 45,
            value: min
          },
          max: {
            range: Math.floor(max / 90) * 90 + 45,
            value: max
          }
        }
      } else {
        for (const raw of rawData) {
          if (raw.percent === 100) {
            raw.percent -= 0.01
          }
          min = Math.min(raw.percent, min)
          max = Math.max(raw.percent, max)
        }

        return {
          min: {
            range: Math.floor(min / 5) * 5 + 2.5,
            value: min
          },
          max: {
            range: Math.floor(max / 5) * 5 + 2.5,
            value: max === 99.99 ? 100 : max
          }
        }
      }
    },
    currentChartHasOne () {
      return this.panelData.pv.data[this.currentType].top20.length < 2
    },
    currentChartOption () {
      const colors = {
        stayInCount: '#7c9e06',
        dropOff: '#ff4a00',
        rollback: '#9665e3',
        reload: '#0074ff',
        interval: '#4b8f0d'
      }
      const rawData = this.panelData.pv.data[this.currentType].top20
      const data = {}

      if (!this.currentChartHasOne) {
        let cursor = 2.5
        if (this.currentType === 'interval') {
          cursor = 45
          while (cursor < 1800) {
            data[cursor] = 0
            cursor += 90
          }
          for (const raw of rawData) {
            data[Math.floor(this.getTop20Value(raw, this.currentType) / 1000 / 90) * 90 + 45]++
          }
        } else {
          while (cursor < 100) {
            data[cursor] = 0
            cursor += 5
          }
          for (const raw of rawData) {
            data[Math.floor(raw.percent / 5) * 5 + 2.5]++
          }
        }
        for (const idx in data) {
          if (idx < this.currentMinMax.min.range || idx > this.currentMinMax.max.range) {
            delete data[idx]
          }
        }
      } else {
        const rawValue = this.getTop20Value(this.panelData.pv.data[this.currentType].top20[0], this.currentType)
        data[this.currentType === 'interval' ? rawValue / 1000 : rawValue] = 1
      }

      let options = {
        xAxis: {
          min: this.currentType === 'interval' ? -90 : -5,
          max: this.currentType === 'interval' ? 1870 : 100,
          type: 'value',
          splitLine: {
            show: false
          },
          axisLine: {
            lineStyle: {
              color: '#767676'
            }
          },
          minInterval: this.currentType === 'interval' ? 900 : 50,
          maxInterval: this.currentType === 'interval' ? 900 : 50,
          axisLabel: {
            margin: 10,
            color: '#444444',
            fontSize: 12,
            formatter: (value) => {
              if (value >= 0) {
                if (this.currentType === 'interval') {
                  return getTimeString(value)
                }
                return value
              }
            }
          },
          axisTick: {
            show: false
          },
          splitNumber: 2
        },
        yAxis: {
          min: 0,
          minInterval: 1,
          splitNumber: 2,
          name: 'Page',
          nameTextStyle: {
            align: 'right',
            fontSize: '10px',
            color: '#767676',
            padding: [0, 30, 0, 0]
          },
          axisLabel: {
            color: '#444444',
            fontSize: 12,
            margin: 6
          },
          splitLine: {
            show: true
          }
        },
        grid: {
          top: '33.125%',
          left: '8.860759%',
          width: '86.118444%',
          height: '38.125%',
          containLabel: false
        },
        tooltip: {
          show: false
        },
        series: [
          {
            name: '',
            data: Object.entries(data),
            type: 'line',
            areaStyle: {
              opacity: 0.15
            },
            showSymbol: this.currentChartHasOne,
            symbolSize: 6,
            smooth: true,
            silent: true
          }
        ],
        color: [colors[this.currentType]]
      }

      return options
    }
  },
  mounted () {
    this.chartLoading = true
    this.$nextTick(() => {
      this.onResize()
      window.addEventListener('resize', this.onResize)
    })

    this.autoChangChart()
  },
  beforeDestroy () {
    clearInterval(this.intervalChart)
    window.removeEventListener('resize', this.onResize)
  },
  methods: {
    onResize () {
      const windowWidth = window.innerWidth
      this.resizeTime = new Date()
      if (this.resizeTimeout === false) {
        this.resizeTimeout = true
        setTimeout(this.onResizeEnd, this.resizeEndInterval)
      }

      if (windowWidth < 1440) {
        this.chartLoading = true
      }
    },
    onResizeEnd () {
      if (new Date() - this.resizeTime < this.resizeEndInterval) {
        setTimeout(this.onResizeEnd, this.resizeEndInterval)
      } else {
        this.resizeTimeout = false
        this.chartKey++
        setTimeout(() => {
          this.chartLoading = false
        }, 300)
      }
    },
    getPercentage (value, total) {
      return getPercentage(value, total)
    },
    getTimeString (value) {
      return getTimeString(value / 1000)
    },
    getTop20Value (item, type) {
      return type === 'interval' ? item.interval : item.percent
    },
    autoChangChart () {
      let count = 1
      this.intervalChart = setInterval(() => {
        this.changeChart(this.currentData[count++])
        count = count > 4 ? 1 : count
      }, 3000)
    },
    pauseInterval () {
      clearInterval(this.intervalChart)
    },
    changeChart (type) {
      const offset = this.currentData.findIndex(value => type === value)

      const oldData = [...this.currentData]
      this.formerData = oldData
      this.$set(this.currentData, offset, oldData[0])
      this.$set(this.currentData, 0, oldData[offset])

      this.chartLoading = true
      this.chartMovingStart = true
      setTimeout(() => {
        this.chartMoving = offset
      }, 10)
      setTimeout(() => {
        this.chartLoading = false
        this.chartMovingStart = false
        this.chartMoving = false
      }, 510)
    },
    chartPointCalibration (value) {
      if (this.currentChartHasOne) {
        return value
      }
      return value * 0.95 + 2.5
    },
    compareData (target, type) {
      const digit = type === 'stayInCount' || type === 'dropOff' ? 2 : 1
      if (this.stateDefault) return
      let users = target === 'session' ? this.panelData.session[type] : this.panelData.pv.data[type].avgTop20
      let benchmark = target === 'session' ? this.benchmark[type] : this.benchmark.top20Rate[type]
      if (target === 'pv' && type !== 'interval') {
        benchmark = Number(getPercentage(benchmark, 1))
      }

      const state = users.toFixed(1) - benchmark.toFixed(1) > 0 ? 'high' : users - benchmark < 0 ? 'low' : 'equal'
      const rawValue = state === 'equal' ? users : Math.abs(users - benchmark)
      return {
        value: target === 'pv' && type !== 'interval' ? this.fixedInt(rawValue, digit, 1) : rawValue,
        state
      }
    },
    fixedInt (value, digits, intDigits) {
      value = Number(Math.round(`${value}e${digits}`) + `e-${digits}`)
      return Number.isInteger(value) ? value.toFixed(intDigits) : value.toFixed(digits)
    }
  },
  watch: {
    chartLoading (value) {
      if (!value) {
        this.chartKey++
      }
    }
  },
  props: {
    infoData: {
      default: null
    },
    panelData: {
      default: null
    },
    noData: {
      type: Boolean,
      default: false
    },
    highlight: {
      default: null
    },
    benchmark: {
      type: Object,
      default: null
    },
    domainCategory: {
      type: Object,
      default: null
    },
    domainSubCategory: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      types: {
        stayInCount: 'stayInCount',
        dropOff: 'dropOff',
        rollback: 'rollback',
        reload: 'reloadCount',
        interval: 'avgStay'
      },
      currentData: [
        'stayInCount',
        'dropOff',
        'rollback',
        'reload',
        'interval'
      ],
      formerData: [
        'stayInCount',
        'dropOff',
        'rollback',
        'reload',
        'interval'
      ],
      resizeTime: null,
      resizeTimeout: false,
      resizeEndInterval: 300,
      chartKey: 0,
      chartLoading: false,
      chartMoving: false,
      chartMovingStart: false,
      intervalChart: null,
      DOMAIN_CATEGORY_ICON
    }
  }
}
</script>

<style lang="scss" scoped>
$categoryText: #0185FF;
$categoryBg: #E5F3FF;
$rankValueText1: #0C86B0;
$rankValueText2: #52A8C5;
.ba-ranking-card {
  height: 100%;
  word-break: keep-all;
  display: flex;
  gap: 15px;
  &.ja {
    word-break: normal;

    .panels-wrap {
      &.session {
        .panels {
          .card-panel {
            &.page-per-session {
              h5 {
                line-height: 1.33;
                font-size: 12px;
                margin-top: 4px;

                @media screen and (max-width: 1400px) {
                  font-size: 14px;
                  line-height: 1.43;
                  margin-top: 0;
                }

                span {
                  white-space: nowrap;
                }

                .icon {
                  margin-top: 13px;

                  @media screen and (max-width: 1400px) {
                    margin-top: 8px;
                  }
                }
              }
            }

            &.duration-per-session {
              h5 {
                font-size: 13px;

                @media screen and (max-width: 1400px) {
                  font-size: 12px;
                }
              }
            }
          }
        }
      }
    }
  }

  .card-panel {
    border-radius: 14px;
    box-shadow: 0 2px 4px -2px rgba(0, 0, 0, 0.35);
    background-color: #fff;
  }

  .card-title {
    font-size: 15px;
    font-weight: 500;
    line-height: 0.93;
    color: #444;
    padding: 11px 11px 16px;
    overflow: hidden;
  }

  .no-data {
    position: relative;
    text-align: center;

    .icon {
      margin-top: -10px;
    }

    .no-data-title {
      font-size: 14px;
      font-weight: 500;
      line-height: 1.36;
      color: #ec0047;
      margin: 3px 0 8px;
    }

    .no-data-description {
      font-size: 13px;
      line-height: 1;
      color: #555555;
    }
  }

  .panels-wrap {
    position: relative;
    height: 100%;

    .panels {
      position: relative;
      height: calc(100% - 40px);
    }

    &.session {
      min-width: 430px;
      .card-title /deep/ em {
        color: #0c86b0;
      }

      .panels {
        display: flex;
        flex-direction: column;
        gap: 15px;
        flex: 1 1 auto;
        .card-panel {
          // height: 182px;

          display: flex;
          flex-direction: column;
          gap: 15px;
          flex: 1 1 auto;
          padding: 10px;
          .contents-wrap {
            flex-grow: 1;
            padding: 15px 15px 0;
            .title {
              color:#444;
              font-size: 14px;
              line-height: 1;
              margin-bottom: 15px;
              font-weight: 500;

              &.no-benchmark {
                margin-bottom: 30px;
              }
            }
            .contents {
              display: flex;
              align-items: center;
              gap: 14px;

              font-size: 13px;
              color: #666;
              font-weight: 400;
              line-height: 1.5;
              > i {
                flex-shrink: 0;
              }
              .benchmark-contents {
                .contents-text {
                  width: 191px;
                  word-break: keep-all;
                  line-height: 1.2;
                  span {
                    display: inline-block;
                    width:100%;
                    color: #444;
                  }
                  &:lang(ja) {
                    letter-spacing: -0.5px;
                  }
                }
              }
              .value-wrap {
                flex-grow: 1;
                text-align: center;
                .value {
                  font-weight: 500;
                  line-height: 1.2;
                  color: $rankValueText1;
                }
                .unit {
                  font-size: 12px;
                  color: #767676;
                }
                span {
                  display: block;
                }
              }
            }
          }
          .benchmark-info {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 5px;
            border-radius: 5px;
            background-color: $categoryBg;
            .category-icon {
              display: flex;
              justify-content: center;
              align-items: center;
              width: 18px;
              height: 18px;
              border-radius: 3px;
              background: #8AC7FF;
            }
            .category-name {
              font-size: 13px;
              font-weight: 500;
              line-height: 1;
              color: $categoryText;
              flex-grow: 1;
              span {
                font-weight: 400;
              }
            }
            .benchmark-value {
              color: $rankValueText1;
              font-size: 16px;
              font-weight: 600;
              line-height: 1;
              &:after {
                content: attr(data-unit);
                padding: 0 8px;
                color: #767676;
                font-weight: 500;
                font-size: 11px;
              }
              &.second {
                color: $rankValueText2
              }
            }
          }
          &.page-per-session {
            .contents {
              .value-wrap {
                .value {
                  font-size: 40px;
                }
              }
            }
            // h5 {
            //   font-size: 14px;
            //   line-height: 1.43;
            //   color: #444444;
            //   float: left;
            //   width: calc(40% - 25px);
            //   padding: 32px 0 0 25px;

            //   @media screen and (max-width: 1400px) {
            //     width: 100%;
            //     padding: 25px 0 0;
            //     text-align: center;
            //     white-space: nowrap;
            //   }

            //   span {
            //     & /deep/ br {
            //       display: block;

            //       @media screen and (max-width: 1400px) {
            //         display: none;
            //       }
            //     }
            //   }

            //   i.icon {
            //     margin-top: 8px;

            //     @media screen and (max-width: 1400px) {
            //       display: none;
            //     }
            //   }
            // }

            // p {
            //   font-size: 12px;
            //   line-height: 1.08;
            //   text-align: center;
            //   color: #767676;
            //   float: left;
            //   width: 52%;
            //   padding-top: 52px;

            //   @media screen and (max-width: 1400px) {
            //     width: 100%;
            //     padding-top: 15px;
            //   }

            //   em {
            //     display: block;
            //     font-size: 52px;
            //     font-weight: 500;
            //     line-height: 52px;
            //     color: #0c86b0;
            //     margin-bottom: 1px;

            //     @media screen and (max-width: 1400px) {
            //       margin-bottom: 3px;
            //     }

            //     &.long {
            //       font-size: 38px;
            //       line-height: 46px;
            //     }
            //   }
            // }
          }

          &.duration-per-session {
            .contents {
              .value-wrap {
                display: flex;
                justify-content: flex-end;
                gap: 20px;
                flex: 1 1 auto;
                height: 50px;

                .value-item {
                  display: flex;
                  flex-direction: column;
                  justify-content: space-between;
                  &.first {
                    .value {
                      font-size: 40px;
                      &.hour {
                        font-size: 28px;
                      }
                    }
                  }
                  &.second {
                    .value {
                      font-size: 14px;
                      color: $rankValueText2;
                    }
                  }
                }
              }
            }
            .benchmark-info {
              .benchmark-value {
                font-size: 14px;
                text-align: right;
                &:after {
                  padding: 5px;
                }
              }
            }
          //   // text-align: center;

          //   h5 {
          //     font-size: 14px;
          //     line-height: 1.43;
          //     color: #444444;
          //     padding-top: 23px;

          //     @media screen and (max-width: 1400px) {
          //       font-size: 13px;
          //     }
          //   }

          //   p {
          //     font-size: 15px;
          //     line-height: 1;
          //     color: #444;
          //     font-weight: 500;
          //     margin-top: 9px;

          //     em {
          //       display: block;
          //       font-size: 38px;
          //       font-weight: 500;
          //       line-height: 38px;
          //       color: #444;
          //       margin-top: 4px;

          //       &.hour {
          //         font-size: 26px;
          //       }
          //     }
          //   }

          //   h6 {
          //     font-size: 12px;
          //     line-height: 1;
          //     color: #767676;
          //     margin-top: 14px;
          //   }
          }
        }

        // &::after {
        //   content: "";
        //   display: block;
        //   position: absolute;
        //   top: 5px;
        //   right: -16px;
        //   width: 1px;
        //   height: 24px;
        //   background-color: #d7d7d7;
        // }
      }
    }

    &.pv {
      flex-grow: 1;
      &.full {
        width: 100%;
      }
      .guide-link {
        cursor: pointer;
        position: absolute;
        right: 0;
        bottom: 100%;
        padding: 10px;
        margin-bottom: 3px;
        font-size: 13px;
        a {
          color: #0074FF;
          display: flex;
          gap: 8px;
          align-items: center;
          line-height: 1;
        }
      }
      .card-panel {
        height: 100%;
        width: 100%;

        &.pv-charts {
          position: relative;
          display: flex;

          .chart-first {
            position: relative;
            vertical-align: top;
            display: inline-block;
            height: 100%;
            flex-grow: 1;
            transition: width 0.2s ease;
            overflow: hidden;

            @media screen and (max-width: 1400px) {
              width: 62.741376%;
            }

            @media screen and (max-width: 1360px) {
              width: 63.583309%;
            }

            @media screen and (max-width: 1320px) {
              width: 64.425243%;
            }

            @media screen and (max-width: 1280px) {
              width: 65.267176%;
            }

            h5 {
              margin: 25px 25px 20px;
              font-size: 14px;
              line-height: 1;
              color: #444;
            }
            .benchmarked-description {
              margin-left: 25px;
              max-width: 270px;

              .benchmark-text {
                font-size: 24px;
                font-weight: 600;
                line-height: 1.2;
                &:lang(ja) {
                  font-size: 18px;
                }
                &:lang(en) {
                  font-size: 18px;
                }
              }
            }
            .chart-description {
              position: relative;
              margin: 21px 0 0 25px;
              width: calc(100% - 320px);
              font-size: 13px;
              line-height: 1.31;
              color: #888;

              &.with-warning {
                margin-left: 50px;
              }

              i.icon {
                position: absolute;
                top: -2px;
                left: -26px;
              }

              .warning {
                color: #9d6a00;
                margin-bottom: 10px;
              }
            }

            .chart-value {
              position: absolute;
              top: 73px;
              right: 25px;

              span {
                font-size: 12px;
                line-height: 1;
                color: #767676;
                display: block;
                padding-left: 5px;
                margin-bottom: 3px;
              }

              em {
                font-size: 30px;
                font-weight: 500;
                line-height: 1;
              }

              i {
                margin: 2px 0 0 -8px;
              }
            }

            .chart {
              position: absolute;
              left: 0;
              bottom: 0;
              width: 100%;
              height: 160px;

              &::after {
                content: "";
                position: absolute;
                bottom: 45px;
                left: 8.860760%;
                width: 86.618444%;
                border-bottom: 1px solid #767676;
              }

              .chart-wrap {
                height: 100%;
                transition: opacity 0.3s ease;
              }

              &.loading {
                .chart-wrap {
                  opacity: 0;
                }
              }

              .more {
                position: absolute;
                bottom: 28.125%;
                left: calc(12.658227% + 1px);
                width: 79.023508%;
                height: 100%;
                z-index: 2;

                .line {
                  position: absolute;
                  bottom: 0;
                  height: 56.25%;
                  border-left: dotted 1px;
                  opacity: 0.65;

                  &.min {
                    height: 41.875%;
                    border-color: #000000 !important;
                    opacity: 0.25;
                  }

                  &.max {
                    height: 69.375%;
                    border-color: #000000 !important;
                    opacity: 0.25;
                  }
                  &.benchmark {
                    height: 85%;
                    border-color: #0185FF !important;
                    opacity: 0.25;
                  }
                }

                .text {
                  position: absolute;
                  bottom: 91px;
                  border-radius: 4px;
                  transform: translateX(-50%);
                  white-space: nowrap;
                  padding: 6px 8px;
                  color: #ffffff;
                  box-shadow: 0 8px 15px -2px rgba(0, 0, 0, 0.1);

                  &.min {
                    bottom: 68px;
                    background-color: #fff;
                    color: #666666;
                    box-shadow: 0 8px 15px -2px rgba(0, 0, 0, 0.06), 0 0 0 1px rgba(0, 0, 0, 0.12);
                  }

                  &.max {
                    bottom: 112px;
                    background-color: #fff;
                    color: #666666;
                    box-shadow: 0 8px 15px -2px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.2);
                  }
                  &.benchmark {
                    display: flex;
                    align-items: center;
                    gap: 5px;
                    bottom: 132px;
                    background-color: #E5F3FF;
                    color: #666666;
                    box-shadow: 0 8px 15px -2px rgba(0, 0, 0, 0.06), 0 0 0 1px rgba(18, 18, 18, 0.24);
                    border-radius: 4px;
                    color: $categoryText;
                    i.icon {
                      font-size: 14px;
                    }
                  }
                }
              }
            }
          }

          .chart-others {
            vertical-align: top;
            display: inline-block;
            width: 38.100558%;
            transition: width 0.2s ease;

            @media screen and (max-width: 1400px) {
              width: 37.258624%;
            }

            @media screen and (max-width: 1360px) {
              width: 36.416691%;
            }

            @media screen and (max-width: 1320px) {
              width: 35.574757%;
            }

            @media screen and (max-width: 1280px) {
              width: 34.732824%;
            }
          }

          .chart-second {
            position: relative;
            display: inline-block;
            width: calc(50% - 1px);
            margin-left: 1px;
            height: 185px;
            overflow: hidden;
            cursor: pointer;
            background-color: #fff;
            box-shadow: 0 0 0 0 rgba(255, 255, 255, 0), -1px 0 0 0 rgba(235, 235, 235, 1);
            transition: box-shadow 0.2s ease;

            &.chart-1 {
              height: 184px;
              box-shadow: 0 0 0 0 rgba(235, 235, 235, 1), -0.5px 0.5px 0 0.5px rgba(235, 235, 235, 1);
            }

            &.chart-2 {
              height: 184px;
              box-shadow: 0 0 0 0 rgba(235, 235, 235, 1), -0.5px 0.5px 0 0.5px rgba(235, 235, 235, 1);
              border-top-right-radius: 14px;
            }

            &.chart-3 {
              margin-top: -1px;
            }

            &.chart-4 {
              margin-top: -1px;
              border-bottom-right-radius: 14px;
            }

            &:hover {
              box-shadow: 0 8px 22px -8px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(0, 0, 0, 0.12);
              z-index: 2;
            }

            h5 {
              margin: 22px 20px 37px 23px;
              font-size: 14px;
              line-height: 1.5;
              color: #444;
              height: 14px;

              .gap--warning {
                display: inline-block;
                width: 2px;
              }

              i.icon {
                vertical-align: middle;
                margin: -8px 0 -5px;
              }
            }

            .chart-value {
              margin: 35px 0 0 25px;

              span {
                font-size: 12px;
                line-height: 1;
                color: #767676;
                display: block;
                padding-left: 2px;
                margin-bottom: 2px;
              }

              em {
                display: block;
                font-size: 30px;
                font-weight: 500;
                line-height: 30px;
              }

              &.long {
                em {
                  @media screen and (max-width: 1400px) {
                    font-size: 22px;
                  }
                }
              }

              i {
                margin: 3px 0 0 0;
              }
            }
          }

          .stayInCount {
            .benchmark-text {
              color: #81a507;
            }
            .chart {
              .more {
                .text.avg {
                  background-color: #81a507;
                }

                .line {
                  border-color: #81a507;
                }
              }
            }
            .chart-value {
              em {
                color: #81a507;
              }
            }
          }

          .dropOff {
            .benchmark-text {
              color: #ff4a00;
            }
            .chart {
              .more {
                .text.avg {
                  background-color: #ff4a00;
                }

                .line {
                  border-color: #ff4a00;
                }
              }
            }
            .chart-value {
              em {
                color: #ff4a00;
              }
            }
          }

          .rollback {
            .benchmark-text {
              color: #9665e3;
            }
            .chart {
              .more {
                .text.avg {
                  background-color: #9665e3;
                }

                .line {
                  border-color: #9665e3;
                }
              }
            }
            .chart-value {
              em {
                color: #9665e3;
              }
            }
          }

          .reload {
            .benchmark-text {
              color: #0074ff;
            }
            .chart {
              .more {
                .text.avg {
                  background-color: #0074ff;
                }

                .line {
                  border-color: #0074ff;
                }
              }
            }
            .chart-value {
              em {
                color: #0074ff;
              }
            }
          }

          .interval {
            .benchmark-text {
              color: #4b8f0d;
            }
            .chart {
              .more {
                .text.avg {
                  background-color: #4b8f0d;
                }

                .line {
                  border-color: #4b8f0d;
                }
              }
            }
            .chart-value {
              em {
                color: #4b8f0d;
              }
            }
          }
        }

        .chart-pane {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;

          &.old {
            left: 150%;
          }
        }

        .moving-start {
          .chart-pane {
            left: 150%;

            &.old {
              left: 0;
            }
          }
        }

        .moving {
          .chart-pane {
            left: 0;
            transition: left 0.6s cubic-bezier(0.25, 1, 0.5, 1);

            &.old {
              left: -150%;
            }
          }
        }
      }

      .card-title /deep/ em {
        color: #ec0147;
      }
    }
  }
}
</style>
